import React, { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { settlementLabel } from "@/lib/orderLabels";
import { Receipt, FileSignature, BadgeCheck, AlertOctagon, ShieldCheck, Calculator, Paperclip, FileText, Hash } from "lucide-react";
import MobileSheet from "@/components/MobileSheet";
import MarkPaidModal from "@/components/MarkPaidModal";

const TAX_SCHEMES = [
  { id: "gst_hst", label: "GST/HST (federal)" },
  { id: "gst_pst", label: "GST + PST (BC/SK/MB/QC)" },
  { id: "qst", label: "GST + QST (Quebec)" },
  { id: "exempt", label: "Tax exempt / zero-rated" },
  { id: "other", label: "Other / custom" },
];

const SETTLEMENT_TONE = {
  awaiting_invoice: "var(--ink-mute)",
  invoiced: "var(--amber-500)",
  buyer_confirmed_paid: "#3b82f6",
  settled: "var(--emerald)",
  disputed: "var(--red)",
};

export default function OrderBillingPanel({ order, role, onChanged }) {
  const settlement = order.settlement || { status: "awaiting_invoice", history: [] };
  const tax = order.tax;
  const invoice = order.invoice;
  const subtotal = order.subtotal_cad ?? order.total_price ?? 0;
  const taxTotal = tax?.tax_total ?? 0;
  const grandTotal = order.grand_total_cad ?? (subtotal + taxTotal);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showTaxForm, setShowTaxForm] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showDispute, setShowDispute] = useState(false);

  const action = async (fn) => {
    setBusy(true); setErr("");
    try { await fn(); onChanged?.(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const isSupplier = role === "supplier";
  const isBuyer = role === "buyer";
  const isAdmin = role === "admin";

  const canSetTax = isSupplier && settlement.status !== "settled" && settlement.status !== "disputed";
  const canSubmitInvoice = isSupplier && (settlement.status === "awaiting_invoice" || settlement.status === "invoiced");
  const canMarkPaid = isBuyer && (settlement.status === "invoiced" || settlement.status === "awaiting_invoice");
  const canAcknowledge = isSupplier && settlement.status === "buyer_confirmed_paid";
  const canDispute = (isBuyer || isSupplier) && settlement.status !== "settled" && settlement.status !== "disputed";

  return (
    <div className="card" style={{ padding: 22, marginTop: 24 }} data-testid="order-billing-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        <div>
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)" }}>
            {isSupplier ? "INVOICE, TAX & PAYMENT" : isBuyer ? "INVOICE & PAYMENT" : "INVOICE, TAX & PAYMENT (TRADE)"}
          </div>
          <div className="font-serif-display" style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
            {settlementLabel(settlement.status, role) || settlement.status}
          </div>
        </div>
        <span data-testid="settlement-status-pill" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: "rgba(255,255,255,.04)", color: SETTLEMENT_TONE[settlement.status] || "var(--ink-mute)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".08em" }}>
          {settlementShortTag(settlement.status, role)}
        </span>
      </div>

      {/* A7 — platform-vs-trade banner */}
      <div style={{ padding: 12, background: "rgba(255,255,255,.03)", borderLeft: "3px solid var(--ink-mute)", marginBottom: 18, fontSize: 12, color: "var(--ink-dim)", display: "flex", alignItems: "flex-start", gap: 10 }} data-testid="platform-vs-trade-banner">
        <ShieldCheck size={14} color="var(--ink-mute)" style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          {isBuyer && <><b style={{ color: "var(--ink)" }}>Pay your supplier directly</b> via wire / EFT / cheque. The platform records invoice and payment details so both parties stay in sync — it never holds, escrows, or transmits funds.</>}
          {isSupplier && <><b style={{ color: "var(--ink)" }}>Buyer pays you directly</b> via wire / EFT / cheque. The platform records invoice and payment details for shared reference only — it never holds, escrows, or transmits funds.</>}
          {isAdmin && <><b style={{ color: "var(--ink)" }}>Trade payments stay off-platform.</b> The platform records invoice, tax, and settlement state for clarity but never holds, escrows, or transmits funds. Buyer pays supplier directly via wire/EFT/cheque.</>}
        </span>
      </div>

      {/* Money summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 18 }}>
        <Stat label="Subtotal (CAD)" value={`$${(+subtotal).toFixed(2)}`} />
        <Stat label="Tax" value={tax ? `$${taxTotal.toFixed(2)}` : "—"} muted={!tax} />
        <Stat label="Grand total" value={`$${(+grandTotal).toFixed(2)}`} accent />
        <Stat label="Currency" value={order.currency || "CAD"} muted />
      </div>

      {/* Tax detail */}
      <Section title="Tax breakdown" icon={Calculator}>
        {!tax ? (
          <Empty text="Supplier hasn't entered a tax breakdown yet." />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, fontSize: 13 }}>
            <KV k="Scheme" v={(TAX_SCHEMES.find(s => s.id === tax.scheme)?.label) || tax.scheme} />
            {tax.gst_amount > 0 && <KV k={`GST ${tax.gst_rate}%`} v={`$${tax.gst_amount.toFixed(2)}`} />}
            {tax.hst_amount > 0 && <KV k={`HST ${tax.hst_rate}%`} v={`$${tax.hst_amount.toFixed(2)}`} />}
            {tax.pst_amount > 0 && <KV k={`PST ${tax.pst_rate}%`} v={`$${tax.pst_amount.toFixed(2)}`} />}
            {tax.qst_amount > 0 && <KV k={`QST ${tax.qst_rate}%`} v={`$${tax.qst_amount.toFixed(2)}`} />}
            {tax.notes && <KV k="Notes" v={tax.notes} />}
          </div>
        )}
        {canSetTax && !showTaxForm && (
          <button data-testid="open-tax-form" className="btn btn-ghost form-trigger-btn" onClick={() => setShowTaxForm(true)}>{tax ? "Edit tax" : "Set tax breakdown"}</button>
        )}
        <MobileSheet open={showTaxForm} onClose={() => setShowTaxForm(false)} title={tax ? "EDIT TAX BREAKDOWN" : "SET TAX BREAKDOWN"} testId="tax-form-sheet">
          <TaxForm subtotal={+subtotal} initial={tax} onCancel={() => setShowTaxForm(false)} onSave={(body) => action(async () => { await api.put(`/orders/${order.id}/tax`, body); setShowTaxForm(false); })} busy={busy} />
        </MobileSheet>
      </Section>

      {/* Invoice detail */}
      <Section title="Supplier invoice" icon={Receipt}>
        {!invoice ? (
          <Empty text="Supplier hasn't submitted an invoice yet." />
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, fontSize: 13 }}>
              <KV k="Invoice #" v={<span className="font-mono" style={{ color: "var(--amber-400)" }}>{invoice.supplier_invoice_number}</span>} />
              <KV k="Invoice date" v={shortDate(invoice.supplier_invoice_date)} />
              <KV k="Payment terms" v={invoice.payment_terms} />
              <KV k="Due date" v={shortDate(invoice.due_date) || "—"} />
            </div>
            <InvoiceAttachment invoice={invoice} />
          </>
        )}
        {canSubmitInvoice && !showInvoiceForm && (
          <button data-testid="open-invoice-form" className="btn btn-primary form-trigger-btn" onClick={() => setShowInvoiceForm(true)}>
            <FileSignature size={16} /> {invoice ? "Re-submit invoice" : "Submit invoice"}
          </button>
        )}
        <MobileSheet open={showInvoiceForm} onClose={() => setShowInvoiceForm(false)} title={invoice ? "RE-SUBMIT INVOICE" : "SUBMIT INVOICE"} testId="invoice-form-sheet">
          <InvoiceForm orderId={order.id} initial={invoice} onCancel={() => setShowInvoiceForm(false)} onSave={(body) => action(async () => { await api.post(`/orders/${order.id}/invoice`, body); setShowInvoiceForm(false); })} busy={busy} />
        </MobileSheet>
      </Section>

      {/* Settlement actions */}
      <Section title={isBuyer ? "Payment" : isSupplier ? "Payment confirmation" : "Settlement"} icon={BadgeCheck}>
        <div className="panel-action-row">
          {canMarkPaid && (
            <BuyerPaidButton orderId={order.id} onSuccess={onChanged} busy={busy} />
          )}
          {canAcknowledge && (
            <button data-testid="acknowledge-btn" disabled={busy} className="btn btn-primary" onClick={() => action(async () => { await api.post(`/orders/${order.id}/acknowledge-payment`, {}); })}>
              <BadgeCheck size={14} /> Confirm payment received
            </button>
          )}
          {canDispute && (
            <button data-testid="open-dispute-btn" disabled={busy} className="btn btn-danger" onClick={() => setShowDispute(true)}>
              <AlertOctagon size={14} /> Open dispute
            </button>
          )}
          {isAdmin && <span style={{ color: "var(--ink-mute)", fontSize: 12, alignSelf: "center" }}>Admin view — read-only. Platform never moves trade funds.</span>}
        </div>
        <PaymentReferenceCard settlement={settlement} role={role} />
        <MobileSheet open={showDispute} onClose={() => setShowDispute(false)} title="OPEN DISPUTE" testId="dispute-form-sheet">
          <DisputeForm onCancel={() => setShowDispute(false)} onSave={(body) => action(async () => { await api.post(`/orders/${order.id}/dispute`, body); setShowDispute(false); })} busy={busy} />
        </MobileSheet>
        {/* Settlement timeline */}
        <SettlementTimeline history={settlement.history || []} />
      </Section>

      {err && <div style={{ color: "var(--red)", fontSize: 13, marginTop: 10 }} data-testid="billing-panel-error">{err}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div style={{ borderTop: "1px dashed var(--line)", paddingTop: 16, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        {Icon && <Icon size={14} color="var(--amber-500)" />}
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)" }}>{title.toUpperCase()}</div>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, accent, muted }) {
  return (
    <div className="card" style={{ padding: 12, background: "rgba(255,255,255,.02)" }}>
      <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--ink-mute)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4, color: accent ? "var(--amber-400)" : muted ? "var(--ink-mute)" : "var(--ink)" }}>{value}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".08em", color: "var(--ink-mute)", textTransform: "uppercase" }}>{k}</div>
      <div style={{ fontSize: 13, marginTop: 2 }}>{v}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div style={{ color: "var(--ink-mute)", fontSize: 13, fontStyle: "italic" }}>{text}</div>;
}

function shortDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function TaxForm({ subtotal, initial, onCancel, onSave, busy }) {
  const [scheme, setScheme] = useState(initial?.scheme || "gst_hst");
  const [gstRate, setGstRate] = useState(initial?.gst_rate ?? 5);
  const [hstRate, setHstRate] = useState(initial?.hst_rate ?? (initial ? 0 : 13));
  const [pstRate, setPstRate] = useState(initial?.pst_rate ?? 0);
  const [qstRate, setQstRate] = useState(initial?.qst_rate ?? 0);
  const [notes, setNotes] = useState(initial?.notes || "");

  const compute = (rate) => +((subtotal * (+rate || 0)) / 100).toFixed(2);
  const gstAmt = scheme === "gst_pst" || scheme === "qst" || scheme === "other" ? compute(gstRate) : (scheme === "gst_hst" ? compute(gstRate) : 0);
  const hstAmt = scheme === "gst_hst" ? compute(hstRate) : 0;
  const pstAmt = scheme === "gst_pst" ? compute(pstRate) : 0;
  const qstAmt = scheme === "qst" ? compute(qstRate) : 0;
  const total = +(gstAmt + hstAmt + pstAmt + qstAmt).toFixed(2);

  const submit = (e) => {
    e.preventDefault();
    onSave({
      scheme,
      gst_rate: +gstRate || 0, gst_amount: gstAmt,
      hst_rate: +hstRate || 0, hst_amount: hstAmt,
      pst_rate: +pstRate || 0, pst_amount: pstAmt,
      qst_rate: +qstRate || 0, qst_amount: qstAmt,
      notes: notes || null,
    });
  };

  return (
    <form onSubmit={submit} className="inline-form" style={{ display: "grid", gap: 10 }}>
      <div><label className="label">Scheme</label>
        <select className="input" data-testid="tax-scheme" value={scheme} onChange={e => setScheme(e.target.value)}>
          {TAX_SCHEMES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        {(scheme === "gst_hst" || scheme === "gst_pst" || scheme === "qst" || scheme === "other") && (
          <div><label className="label">GST rate (%)</label><input className="input" type="number" step="0.01" min="0" data-testid="tax-gst-rate" value={gstRate} onChange={e => setGstRate(e.target.value)} /></div>
        )}
        {scheme === "gst_hst" && (
          <div><label className="label">HST rate (%)</label><input className="input" type="number" step="0.01" min="0" data-testid="tax-hst-rate" value={hstRate} onChange={e => setHstRate(e.target.value)} /></div>
        )}
        {scheme === "gst_pst" && (
          <div><label className="label">PST rate (%)</label><input className="input" type="number" step="0.01" min="0" data-testid="tax-pst-rate" value={pstRate} onChange={e => setPstRate(e.target.value)} /></div>
        )}
        {scheme === "qst" && (
          <div><label className="label">QST rate (%)</label><input className="input" type="number" step="0.01" min="0" data-testid="tax-qst-rate" value={qstRate} onChange={e => setQstRate(e.target.value)} /></div>
        )}
      </div>
      <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>
        Computed tax: <b style={{ color: "var(--amber-400)" }}>${total.toFixed(2)}</b> on subtotal ${subtotal.toFixed(2)}
      </div>
      <div><label className="label">Notes (optional)</label><textarea className="input" style={{ minHeight: 50 }} value={notes} onChange={e => setNotes(e.target.value)} /></div>
      <div className="form-action-row">
        <button data-testid="tax-save" className="btn btn-primary" disabled={busy}>Save tax</button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function PaymentProofLink({ settlement }) {
  const url = settlement?.buyer_payment_proof_url;
  const name = settlement?.buyer_payment_proof_filename;
  const size = settlement?.buyer_payment_proof_size;
  if (!settlement?.buyer_payment_proof_upload_id) return null;
  const href = url
    ? (url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : `${process.env.REACT_APP_BACKEND_URL}/api${url}`)
    : "#";
  return (
    <a data-testid="payment-proof-link" href={href} target="_blank" rel="noreferrer"
       style={{ padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 4, background: "rgba(245,158,11,.05)", display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "var(--ink)" }}>
      <FileText size={16} color="var(--amber-400)" />
      <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
        <div style={{ color: "var(--amber-400)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {name || "View payment proof"}
        </div>
        <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>
          {size ? `${Math.ceil(size / 1024)} KB · open in new tab →` : "open in new tab →"}
        </div>
      </div>
    </a>
  );
}

function InvoiceForm({ orderId, initial, onCancel, onSave, busy }) {
  const [num, setNum] = useState(initial?.supplier_invoice_number || "");
  const [date, setDate] = useState(initial?.supplier_invoice_date?.slice(0, 10) || new Date().toISOString().slice(0, 10));
  const [terms, setTerms] = useState(initial?.payment_terms || "NET 30");
  const [uploadId, setUploadId] = useState(initial?.supplier_invoice_upload_id || null);
  const [uploadMeta, setUploadMeta] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking same file
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setUploadErr("File too large (max 10 MB).");
      return;
    }
    setUploadErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "invoice");
      fd.append("related_id", orderId);
      // Let axios + browser set the multipart Content-Type with boundary
      // (overriding the api instance default of application/json).
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": undefined } });
      setUploadId(data.id);
      setUploadMeta({ name: data.original_filename, size: data.size, mime: data.mime_type });
    } catch (err) {
      setUploadErr(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({
      supplier_invoice_number: num,
      supplier_invoice_date: new Date(date).toISOString(),
      payment_terms: terms,
      supplier_invoice_upload_id: uploadId || null,
    });
  };
  return (
    <form onSubmit={submit} className="inline-form" style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <div><label className="label">Invoice number *</label><input className="input" data-testid="invoice-number" required value={num} onChange={e => setNum(e.target.value)} /></div>
        <div><label className="label">Invoice date *</label><input className="input" type="date" data-testid="invoice-date" required value={date} onChange={e => setDate(e.target.value)} /></div>
        <div><label className="label">Payment terms</label><input className="input" data-testid="invoice-terms" value={terms} onChange={e => setTerms(e.target.value)} placeholder="NET 30" /></div>
      </div>
      <InvoiceFilePicker
        uploadId={uploadId} uploadMeta={uploadMeta} uploading={uploading} error={uploadErr}
        existingName={initial?.supplier_invoice_upload_id && !uploadMeta ? "Previously attached invoice" : null}
        onPick={onPickFile} onClear={() => { setUploadId(null); setUploadMeta(null); }} />
      <div style={{ fontSize: 12, color: "var(--ink-mute)" }}>Buyer pays the supplier off-platform. The platform records this invoice (and the attached document) for shared reference only.</div>
      <div className="form-action-row">
        <button data-testid="invoice-save" className="btn btn-primary" disabled={busy || uploading}>{uploading ? "Uploading…" : "Submit invoice"}</button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function InvoiceFilePicker({ uploadId, uploadMeta, uploading, error, existingName, onPick, onClear }) {
  const inputRef = useRef(null);
  const hasFile = !!uploadId;
  return (
    <div>
      <label className="label">Invoice document (PDF or image)</label>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp,image/gif"
        onChange={onPick}
        disabled={uploading}
        data-testid="invoice-file-input"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
      {!hasFile ? (
        <button
          type="button"
          data-testid="invoice-file-pick"
          className="btn btn-ghost form-trigger-btn"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={uploading}
          style={{ margin: 0 }}
        >
          <Paperclip size={16} /> {uploading ? "Uploading…" : "Attach invoice document"}
        </button>
      ) : (
        <div data-testid="invoice-file-attached" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 4, background: "rgba(245,158,11,.05)" }}>
          <FileText size={16} color="var(--amber-400)" />
          <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
            <div style={{ color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {uploadMeta?.name || existingName || "Invoice attached"}
            </div>
            {uploadMeta?.size && <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{Math.ceil(uploadMeta.size / 1024)} KB</div>}
          </div>
          <button type="button" data-testid="invoice-file-clear" className="btn btn-ghost" style={{ padding: "6px 10px", fontSize: 11 }} onClick={onClear}>Replace</button>
        </div>
      )}
      {error && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 6 }} data-testid="invoice-file-error">{error}</div>}
    </div>
  );
}

function InvoiceAttachment({ invoice }) {
  const url = invoice?.supplier_invoice_upload_url;
  const name = invoice?.supplier_invoice_upload_filename;
  const size = invoice?.supplier_invoice_upload_size;
  if (!invoice?.supplier_invoice_upload_id) {
    return (
      <div style={{ marginTop: 10, padding: "10px 12px", border: "1px dashed var(--line)", borderRadius: 4, color: "var(--ink-mute)", fontSize: 12, display: "flex", alignItems: "center", gap: 8 }} data-testid="invoice-no-attachment">
        <Paperclip size={14} /> No invoice document attached.
      </div>
    );
  }
  const href = url
    ? (url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : `${process.env.REACT_APP_BACKEND_URL}/api${url}`)
    : "#";
  return (
    <a data-testid="invoice-attachment-link"
       href={href}
       target="_blank" rel="noreferrer"
       style={{ marginTop: 10, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 4, background: "rgba(245,158,11,.05)", display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "var(--ink)" }}>
      <FileText size={16} color="var(--amber-400)" />
      <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
        <div style={{ color: "var(--amber-400)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {name || "View invoice document"}
        </div>
        <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>
          {size ? `${Math.ceil(size / 1024)} KB · open in new tab →` : "open in new tab →"}
        </div>
      </div>
    </a>
  );
}

function BuyerPaidButton({ orderId, onSuccess, busy }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="open-mark-paid" className="btn btn-primary" disabled={busy} onClick={() => setOpen(true)}>
        <BadgeCheck size={14} /> Mark payment sent
      </button>
      <MarkPaidModal orderId={orderId} open={open} onClose={() => setOpen(false)} onSuccess={onSuccess} />
    </>
  );
}

/**
 * Compact card surfacing the buyer's payment reference for both sides.
 *
 *  - When settlement.status === "buyer_confirmed_paid":
 *      - Shows a yellow "AWAITING SUPPLIER CONFIRMATION" pill.
 *      - Renders the method, reference, notes, and timestamp prominently so
 *        the supplier can reconcile against their bank statement.
 *  - When settlement.status === "settled":
 *      - Shows a green "PAYMENT CONFIRMED" pill plus the supplier's
 *        acknowledgement timestamp and optional supplier note.
 *  - For any earlier state, the card is hidden (no reference yet).
 */
function PaymentReferenceCard({ settlement, role }) {
  if (!settlement) return null;
  const status = settlement.status;
  const shown = status === "buyer_confirmed_paid" || status === "settled";
  if (!shown) return null;

  const method = settlement.buyer_payment_method;
  const reference = settlement.buyer_payment_reference;
  const paidAt = settlement.buyer_paid_at;
  // Notes live in the last "buyer_confirmed_paid" entry of settlement.history.
  const buyerEntry = [...(settlement.history || [])]
    .reverse()
    .find(h => h.status === "buyer_confirmed_paid");
  const buyerNotes = buyerEntry?.notes;
  const supAt = settlement.supplier_acknowledged_at;
  const supNotes = settlement.supplier_notes;

  const confirmed = status === "settled";
  const pillBg = confirmed ? "rgba(16,185,129,.08)" : "rgba(245,158,11,.08)";
  const pillBorder = confirmed ? "1px solid rgba(16,185,129,.35)" : "1px solid rgba(245,158,11,.35)";
  const pillColor = confirmed ? "var(--emerald)" : "var(--amber-400)";
  const pillLabel = confirmed
    ? "PAYMENT CONFIRMED"
    : (role === "supplier" ? "AWAITING YOUR CONFIRMATION" : "AWAITING SUPPLIER CONFIRMATION");

  return (
    <div data-testid="payment-reference-card"
         style={{ marginTop: 12, padding: 14, borderRadius: 6, background: pillBg, border: pillBorder, display: "grid", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".12em", color: pillColor }}>
          {pillLabel}
        </div>
        {paidAt && <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>Buyer marked sent · {new Date(paidAt).toLocaleString()}</div>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8, fontSize: 13 }}>
        <KV k="Method" v={method || "—"} />
        <KV k="Reference / txn #" v={reference
          ? <span data-testid="payment-reference-value" className="font-mono" style={{ color: "var(--ink)", wordBreak: "break-all" }}><Hash size={11} style={{ marginRight: 4, color: "var(--amber-400)" }} />{reference}</span>
          : "—"} />
      </div>
      {buyerNotes && <div data-testid="payment-reference-notes" style={{ fontSize: 12, color: "var(--ink-dim)", fontStyle: "italic", padding: "8px 10px", background: "rgba(255,255,255,.03)", borderRadius: 4 }}>
        “{buyerNotes}”
      </div>}

      <PaymentProofLink settlement={settlement} />

      {confirmed && (
        <div style={{ borderTop: "1px dashed var(--line)", paddingTop: 10, marginTop: 2 }}>
          <div className="font-mono" style={{ fontSize: 11, color: "var(--emerald)", letterSpacing: ".08em" }}>
            ✓ SUPPLIER CONFIRMED RECEIPT
            {supAt && <span style={{ color: "var(--ink-mute)", marginLeft: 8, fontSize: 11 }}>· {new Date(supAt).toLocaleString()}</span>}
          </div>
          {supNotes && <div style={{ fontSize: 12, color: "var(--ink-dim)", marginTop: 4 }}>{supNotes}</div>}
        </div>
      )}

      {!confirmed && role === "buyer" && (
        <div style={{ fontSize: 11, color: "var(--ink-mute)" }}>
          You've submitted your payment reference. Your supplier will reconcile it against their bank statement and confirm receipt.
        </div>
      )}
      {!confirmed && role === "supplier" && (
        <div style={{ fontSize: 11, color: "var(--ink-mute)" }}>
          Cross-check this reference against your bank statement, then tap <b>Confirm payment received</b> above to close out settlement.
        </div>
      )}
    </div>
  );
}

function DisputeForm({ onCancel, onSave, busy }) {
  const [reason, setReason] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ reason }); }} className="inline-form" style={{ display: "grid", gap: 10, borderLeft: "3px solid var(--red)", paddingLeft: 12 }}>
      <div className="font-mono" style={{ fontSize: 11, color: "var(--red)", letterSpacing: ".1em" }}>OPEN SETTLEMENT DISPUTE</div>
      <textarea className="input" style={{ minHeight: 70 }} required minLength={3} maxLength={500} value={reason} onChange={e => setReason(e.target.value)} placeholder="Briefly explain the dispute (3–500 chars)…" data-testid="dispute-reason" />
      <div className="form-action-row">
        <button data-testid="dispute-submit" className="btn btn-danger" disabled={busy}>Open dispute &amp; create ticket</button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function SettlementTimeline({ history }) {
  if (!history.length) {
    return <div style={{ color: "var(--ink-mute)", fontSize: 12, fontStyle: "italic" }}>No payment events yet.</div>;
  }
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {history.slice().reverse().map((h, i) => (
        <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12, color: "var(--ink-dim)", flexWrap: "wrap" }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: SETTLEMENT_TONE[h.status] || "var(--ink-mute)", flexShrink: 0 }} />
          <span style={{ color: "var(--ink)" }}>{settlementNeutralPretty(h.status)}</span>
          <span className="font-mono" style={{ color: "var(--ink-mute)" }}>{new Date(h.at).toLocaleString()}</span>
          {h.invoice_number && <span>· inv #{h.invoice_number}</span>}
          {h.method && <span>· {h.method}</span>}
          {h.reference && <span>· ref {h.reference}</span>}
          {h.reason && <span style={{ color: "var(--red)" }}>· {h.reason}</span>}
        </div>
      ))}
    </div>
  );
}

// Short tag shown in the pill in the panel header. Role-aware, no raw enums.
function settlementShortTag(status, role) {
  const buyer = {
    awaiting_invoice: "AWAITING INVOICE",
    invoiced: "INVOICE RECEIVED",
    buyer_confirmed_paid: "PAYMENT SENT",
    settled: "PAYMENT CONFIRMED",
    disputed: "DISPUTED",
  };
  const supplier = {
    awaiting_invoice: "ISSUE INVOICE",
    invoiced: "AWAITING PAYMENT",
    buyer_confirmed_paid: "CONFIRM RECEIPT",
    settled: "PAYMENT RECEIVED",
    disputed: "DISPUTED",
  };
  const neutral = {
    awaiting_invoice: "AWAITING INVOICE",
    invoiced: "INVOICED",
    buyer_confirmed_paid: "BUYER MARKED PAID",
    settled: "SETTLED",
    disputed: "DISPUTED",
  };
  const dict = role === "buyer" ? buyer : role === "supplier" ? supplier : neutral;
  return dict[status] || String(status).replace(/_/g, " ").toUpperCase();
}

// Neutral, human history label for the chronological audit row.
function settlementNeutralPretty(status) {
  const map = {
    awaiting_invoice: "Awaiting invoice",
    invoiced: "Invoice issued",
    buyer_confirmed_paid: "Buyer marked payment sent",
    settled: "Payment confirmed",
    disputed: "Dispute opened",
  };
  return map[status] || String(status).replace(/_/g, " ");
}
